import dayjs from "dayjs"

import { DATE_FORMAT, EMPTY_DATE_PLACEHOLDER } from "@/constants/formats"

export const formatDate = (date?: string | null, format?: string): string =>
  dayjs(date).isValid() ? dayjs(date).format(format || DATE_FORMAT.US) : EMPTY_DATE_PLACEHOLDER

const MINUTE_IN_SECONDS = 60
const HOUR_IN_SECONDS = 60 * MINUTE_IN_SECONDS
const DAY_IN_SECONDS = 24 * HOUR_IN_SECONDS
const MONTH_IN_SECONDS = 30 * DAY_IN_SECONDS
const YEAR_IN_SECONDS = 365 * DAY_IN_SECONDS

const pluralize = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? "" : "s"} ago`

// Written out rather than pulled from dayjs' relativeTime plugin: that plugin renders
// a single day as "a day ago", and configuring it to say "1 day ago" means mutating
// the global "en" locale from a shared util.
const formatRelativeTime = (seconds: number): string => {
  if (seconds < MINUTE_IN_SECONDS) return "just now"
  if (seconds < HOUR_IN_SECONDS) return pluralize(Math.floor(seconds / MINUTE_IN_SECONDS), "minute")
  if (seconds < DAY_IN_SECONDS) return pluralize(Math.floor(seconds / HOUR_IN_SECONDS), "hour")
  if (seconds < MONTH_IN_SECONDS) return pluralize(Math.floor(seconds / DAY_IN_SECONDS), "day")
  if (seconds < YEAR_IN_SECONDS) return pluralize(Math.floor(seconds / MONTH_IN_SECONDS), "month")

  return pluralize(Math.floor(seconds / YEAR_IN_SECONDS), "year")
}

// Mail-client style timestamp, e.g. "Sun, May 25, 5:10 PM (1 day ago)".
export const formatEmailTimestamp = (date?: string | null): string => {
  // Guarded explicitly: dayjs(undefined) resolves to "now", so an absent
  // timestamp would otherwise render as the current time.
  if (!date) return EMPTY_DATE_PLACEHOLDER

  const parsed = dayjs(date)
  if (!parsed.isValid()) return EMPTY_DATE_PLACEHOLDER

  const elapsedSeconds = Math.max(0, dayjs().diff(parsed, "second"))

  return `${parsed.format(DATE_FORMAT.EMAIL_TIMESTAMP)} (${formatRelativeTime(elapsedSeconds)})`
}

export function formatMaskedDate(valueToFormat: string, maskToUse: string): string {
  const MASK_USER_INPUT_SYMBOL = "_"
  const ACCEPT_REGEX = /\d/g
  let outputCharIndex = 0

  return valueToFormat
    .split("")
    .map((character, characterIndex) => {
      ACCEPT_REGEX.lastIndex = 0

      if (outputCharIndex > maskToUse.length - 1) {
        return ""
      }

      const maskChar = maskToUse[outputCharIndex]
      const nextMaskChar = maskToUse[outputCharIndex + 1]

      const acceptedChar = ACCEPT_REGEX.test(character) ? character : ""
      const formattedChar = maskChar === MASK_USER_INPUT_SYMBOL ? acceptedChar : maskChar + acceptedChar

      outputCharIndex += formattedChar.length

      const isLastCharacter = characterIndex === valueToFormat.length - 1
      if (isLastCharacter && nextMaskChar && nextMaskChar !== MASK_USER_INPUT_SYMBOL) {
        // when cursor at the end of mask part (e.g. month) prerender next symbol "21" -> "21/"
        return formattedChar ? formattedChar + nextMaskChar : ""
      }

      return formattedChar
    })
    .join("")
}

export const isAfterDate = (date: string, referenceDate: string): boolean => dayjs(date).isAfter(referenceDate, "day")

export const getDateDaysAgo = (days: number): string => dayjs().subtract(days, "day").format(DATE_FORMAT.ISO_18601)
